import { useEffect, useMemo, useRef, useState } from "react";
import { Phone, QrCode, X, ScanLine, UserPlus } from "lucide-react";
import { api, unwrap } from "@/lib/api";
import { MemberBadge } from "@/components/MemberBadge";

/**
 * ClientLookupBar — 56px-tall search at the top of the Receptionist view.
 * - Phone digits → /api/clients/search?phone=...
 * - Empty result → "Add Client" CTA
 * - Click row → calls onSelectClient(client)
 */
export function ClientLookupBar({ onSelectClient, onAddNew }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);  // null = not searched yet; [] = no matches
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  const isPhone = useMemo(() => /^[\d\s+\-()]*$/.test(query) && /\d/.test(query), [query]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const digitsOnly = query.replace(/\D/g, "");
    if (digitsOnly.length < 4 && !/[a-zA-Z]/.test(query)) {
      setResults(null);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const params = isPhone ? { phone: digitsOnly } : { q: query };
        const r = await api.get("/clients/search", { params });
        setResults(unwrap(r) || []);
        setOpen(true);
      } finally {
        setLoading(false);
      }
    }, 280);
  }, [query, isPhone]);

  // click outside to close
  useEffect(() => {
    const h = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const empty = results !== null && results.length === 0;

  return (
    <div className="relative w-full" ref={wrapRef} data-testid="client-lookup">
      <div className="lookup-bar">
        <Phone size={20} className="text-[var(--color-mauve)]" />
        <input
          autoComplete="off"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results !== null && setOpen(true)}
          placeholder="Type mobile number or name to find client…"
          className={isPhone ? "is-mono" : ""}
          data-testid="client-lookup-input"
        />
        {loading && <span className="font-mono text-[11px] text-[var(--color-text-muted)]">…</span>}
        {query && (
          <button
            onClick={() => { setQuery(""); setResults(null); setOpen(false); }}
            className="text-[var(--color-text-muted)]"
            aria-label="Clear"
            data-testid="client-lookup-clear"
          >
            <X size={16} />
          </button>
        )}
        <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 rounded-full glass-subtle font-inter text-[11.5px] text-[var(--color-text-muted)]">
          <ScanLine size={12} /> or scan QR
        </span>
      </div>

      {open && results !== null && (
        <div className="lookup-results" data-testid="client-lookup-results">
          {empty ? (
            <div className="px-5 py-5 flex items-center justify-between gap-4">
              <div>
                <div className="font-sora font-semibold text-[14px]">New client — not in system</div>
                <div className="font-inter text-[12.5px] text-[var(--color-text-muted)] mt-1">
                  We couldn't find {isPhone ? "this number" : "this name"}. Add to keep them on file.
                </div>
              </div>
              <button
                className="btn-3d btn-gold-3d btn-sm"
                onClick={() => { onAddNew?.(isPhone ? query : null); setOpen(false); }}
                data-testid="client-lookup-add-new"
              >
                <UserPlus size={14} /> Add Client
              </button>
            </div>
          ) : (
            results.map((c) => (
              <LookupRow
                key={c.id}
                client={c}
                onPick={() => { onSelectClient?.(c); setOpen(false); setQuery(""); setResults(null); }}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function LookupRow({ client, onPick }) {
  const initials = (client.name || "?")
    .split(" ").filter(Boolean).slice(0, 2)
    .map((w) => w[0]).join("").toUpperCase();
  const lastVisit = client.last_visit_at
    ? Math.floor((Date.now() - new Date(client.last_visit_at).getTime()) / 86400000)
    : null;
  const tags = client.tags || [];
  return (
    <div className="lookup-row" onClick={onPick} data-testid={`client-lookup-row-${client.id}`}>
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center font-sora font-bold text-white text-[13px] flex-shrink-0"
        style={{ background: "linear-gradient(135deg,#B08AAE,#7C5C7A,#5E4060)" }}
      >
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-sora font-semibold text-[14.5px] text-[var(--color-text-primary)] truncate">
            {client.name}
          </span>
          {client.membership && <MemberBadge membership={client.membership} />}
          {tags.includes("VIP") && (
            <span className="status-pill" style={{ background: "rgba(212,168,83,0.14)", color: "#B5892E" }}>VIP</span>
          )}
        </div>
        <div className="flex items-center gap-2 text-[12px] mt-0.5">
          <span className="font-mono text-[var(--color-text-muted)]">{client.phone}</span>
          <span className="text-[var(--color-text-faint)]">·</span>
          <span className="font-inter text-[var(--color-text-muted)]">
            {client.total_visits || 0} visits
            {lastVisit !== null ? ` · Last: ${lastVisit}d ago` : ""}
          </span>
        </div>
      </div>
      <button className="btn-3d btn-gold-3d btn-sm flex-shrink-0">
        Open →
      </button>
    </div>
  );
}
